import { assign } from 'fsm';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { enrichAssociations, enrichProperties, fetchEnrichableAssociations, fetchPropertyUpdates, fetchPropertyDefinitions, fetchPropertySources } from './client';
import { getObjectType, isContactObjectType, wait } from '../../utils';
import { ErrorAlertMessage } from '../../ErrorAlertMessage';
import { usageTracker } from '../../../usageTracker';
import { getEnrichmentTypeFromObjectTypeId } from '../../../utils/getEnrichmentTypeFromEnrichmentId';
import { transformProperties } from './transforms';
import { jsx as _jsx } from "react/jsx-runtime";
export const machine = {
  initial: 'initializing',
  context: {
    enrichAssociatedRecords: false,
    enrichmentType: 'company',
    errorId: null,
    isAutoEnrichmentNudgeOpen: true,
    objectId: '',
    objectTypeId: null,
    onClose: () => {},
    properties: [],
    propertyDefinitions: {},
    propertySources: {},
    propertyUpdates: {},
    selectedProperties: []
  },
  states: {
    initializing: {
      always: [{
        target: 'loadingUpdates',
        actions: ['setEnrichmentType', 'trackOpenPanel']
      }],
      tags: ['loading']
    },
    loadingUpdates: {
      invoke: {
        src: 'fetchPropertyUpdates',
        onDone: [{
          cond: 'hasPropertyUpdates',
          target: 'loadingDefinitions',
          actions: ['setPropertyUpdates']
        }, {
          target: 'idle',
          actions: ['setEmptyProperties']
        }],
        onError: [{
          cond: 'hasValidationError',
          target: 'errorValidation',
          actions: ['setErrorId', 'logError']
        }, {
          cond: 'hasNoEnrichmentData',
          target: 'errorUpdates',
          actions: ['setErrorId', 'logError']
        }, {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }]
      },
      tags: ['loading']
    },
    loadingDefinitions: {
      invoke: {
        src: 'fetchPropertyDefinitions',
        onDone: {
          target: 'loadingSources',
          actions: ['setPropertyDefinitions']
        },
        onError: {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }
      },
      tags: ['loading']
    },
    loadingSources: {
      invoke: {
        src: 'fetchPropertySources',
        onDone: {
          target: 'loadingEnrichableAssociations',
          actions: ['setPropertySources', 'setProperties']
        },
        onError: {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }
      },
      tags: ['loading']
    },
    loadingEnrichableAssociations: {
      invoke: {
        src: 'fetchEnrichableAssociations',
        onDone: {
          target: 'idle',
          actions: ['setEnrichableAssociations']
        },
        onError: {
          target: 'error',
          actions: ['setErrorId', 'logError']
        }
      },
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      },
      tags: ['loading']
    },
    idle: {
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        CLOSE_BANNER: {
          target: 'idle',
          actions: ['closeBanner']
        }
      }
    },
    enriching: {
      invoke: {
        src: 'enrichProperties',
        onDone: [{
          target: 'enrichingAssociated',
          cond: 'shouldEnrichAssociations',
          actions: ['sendUpdateObjectMessage']
        }, {
          target: 'enrichedProperties',
          actions: ['addEnrichSuccess', 'sendUpdateObjectMessage']
        }],
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      }
    },
    enrichingAssociated: {
      invoke: {
        src: 'enrichAssociations',
        onDone: {
          target: 'enrichedProperties',
          actions: ['addEnrichSuccess']
        },
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      }
    },
    enrichedProperties: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'idle',
          actions: ['trackEnrichedRecord', 'closeDrawerAfterEnriched']
        }
      }
    },
    error: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        },
        RETRY: 'loadingUpdates'
      },
      tags: ['error']
    },
    errorEnrichment: {
      on: {
        SELECT_PROPERTIES: {
          target: 'idle',
          actions: ['setSelectedProperties']
        },
        ENRICH: 'enriching',
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      }
    },
    errorUpdates: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      },
      tags: ['error']
    },
    errorValidation: {
      on: {
        CLOSE: {
          target: 'idle',
          actions: ['closeDrawer']
        }
      },
      tags: ['error']
    }
  },
  on: {
    TOGGLE_ENRICH_ASSOCIATED_RECORDS: {
      actions: ['toggleEnrichAssociations']
    }
  },
  actions: {
    addEnrichError: context => {
      const {
        addAlertDanger,
        companyLabelLowercase,
        contactLabelLowercase,
        errorId,
        objectTypeId
      } = context;
      const objectTypeLabel = isContactObjectType(objectTypeId) ? contactLabelLowercase : companyLabelLowercase;
      const Message = () => /*#__PURE__*/_jsx(ErrorAlertMessage, {
        errorId: errorId,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "enrichmentProperties.panel.alerts.error.description"
        })
      });
      addAlertDanger === null || addAlertDanger === void 0 || addAlertDanger({
        message: /*#__PURE__*/_jsx(Message, {}),
        titleText: I18n.text('enrichmentProperties.panel.alerts.error.title', {
          objectType: objectTypeLabel
        })
      });
    },
    addEnrichSuccess: context => {
      const {
        addAlertSuccess,
        companyLabel,
        contactLabel,
        objectTypeId,
        selectedProperties
      } = context;
      const objectTypeLabel = isContactObjectType(objectTypeId) ? contactLabel : companyLabel;
      const Message = () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "enrichmentProperties.panel.alerts.success.description",
        options: {
          count: selectedProperties.length
        }
      });
      addAlertSuccess === null || addAlertSuccess === void 0 || addAlertSuccess({
        message: /*#__PURE__*/_jsx(Message, {}),
        titleText: I18n.text('enrichmentProperties.panel.alerts.success.title', {
          objectType: objectTypeLabel
        })
      });
    },
    closeBanner: assign(() => {
      return {
        isAutoEnrichmentNudgeOpen: false
      };
    }),
    closeDrawer: context => {
      var _context$onClose;
      (_context$onClose = context.onClose) === null || _context$onClose === void 0 || _context$onClose.call(context, false);
    },
    closeDrawerAfterEnriched: context => {
      var _context$onClose2;
      (_context$onClose2 = context.onClose) === null || _context$onClose2 === void 0 || _context$onClose2.call(context, true);
    },
    logError: (_context, event) => {
      const {
        responseJSON
      } = event.data;
      Raven.captureException(responseJSON);
    },
    sendUpdateObjectMessage: context => {
      var _context$sendUpdateOb;
      (_context$sendUpdateOb = context.sendUpdateObjectMessage) === null || _context$sendUpdateOb === void 0 || _context$sendUpdateOb.call(context, context.selectedProperties);
    },
    setEnrichableAssociations: assign((_context, event) => {
      return {
        associatedRecords: event.data.objectIds
      };
    }),
    setEnrichmentType: assign(context => {
      return {
        enrichmentType: getEnrichmentTypeFromObjectTypeId(context.objectTypeId)
      };
    }),
    setErrorId: assign((_context, event) => {
      const {
        responseJSON
      } = event.data;
      const {
        correlationId
      } = responseJSON;
      return {
        errorId: correlationId
      };
    }),
    setEmptyProperties: assign(() => {
      return {
        properties: []
      };
    }),
    setProperties: assign(context => {
      return transformProperties({
        propertyDefinitions: context.propertyDefinitions,
        propertySources: context.propertySources,
        propertyUpdates: context.propertyUpdates
      });
    }),
    setPropertyDefinitions: assign((_context, event) => {
      return {
        propertyDefinitions: event.data
      };
    }),
    setPropertySources: assign((_context, event) => {
      return {
        propertySources: event.data
      };
    }),
    setPropertyUpdates: assign((_context, event) => {
      return {
        propertyUpdates: event.data
      };
    }),
    setSelectedProperties: assign((_context, event) => {
      return {
        selectedProperties: event.data.propertyNames
      };
    }),
    toggleEnrichAssociations: assign(context => {
      return {
        enrichAssociatedRecords: !context.enrichAssociatedRecords
      };
    }),
    trackEnrichedRecord: context => {
      const propertyCount = context.properties.length;
      const selectionCount = context.selectedProperties.length;
      const selectionDifference = propertyCount - selectionCount;
      usageTracker.track('enriched-record', {
        objectType: getObjectType(context.objectTypeId),
        enrichmentType: 'single',
        selectionType: 'manual',
        propertyCount,
        selectionCount,
        selectionDifference,
        includeAssociatedRecords: context.enrichAssociatedRecords
      });
    },
    trackOpenPanel: context => {
      usageTracker.track('interaction', {
        screen: 'enrichment-properties-panel',
        subscreen: getObjectType(context.objectTypeId),
        action: 'opened-enrichment-properties-panel'
      });
    }
  },
  guards: {
    shouldEnrichAssociations: context => {
      var _context$associatedRe, _context$enrichAssoci;
      const hasAssociatedRecords = !!((_context$associatedRe = context.associatedRecords) !== null && _context$associatedRe !== void 0 && _context$associatedRe.length);
      const selectedEnrichAssociatedRecords = (_context$enrichAssoci = context.enrichAssociatedRecords) !== null && _context$enrichAssoci !== void 0 ? _context$enrichAssoci : false;
      return selectedEnrichAssociatedRecords && hasAssociatedRecords;
    },
    hasNoEnrichmentData: (_context, event) => {
      const {
        status,
        responseJSON
      } = event.data;
      const {
        category
      } = responseJSON;
      return status === 404 && category === 'OBJECT_NOT_FOUND';
    },
    hasValidationError: (_context, event) => {
      const {
        status,
        responseJSON
      } = event.data;
      const {
        category
      } = responseJSON;
      return status === 400 && category === 'VALIDATION_ERROR';
    },
    hasPropertyUpdates: (_context, event) => {
      return Object.keys(event.data).length > 0;
    }
  },
  services: {
    enrichAssociations,
    enrichProperties,
    fetchEnrichableAssociations,
    fetchPropertyUpdates,
    fetchPropertyDefinitions,
    fetchPropertySources,
    wait: () => wait()
  }
};